// src/components/common/PrivateRoute.jsx
import React from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { hasRequiredRole, ROLES } from "../../utils/roles";

/**
 * Helper to safely check physical/air-gapped local server configuration
 */
const checkLocalServerVerificationRequired = (currentUser) => {
  if (!currentUser || currentUser.role === ROLES.SUPERADMIN) {
    return false;
  }

  try {
    const companyDataRaw = localStorage.getItem("companyData");
    if (!companyDataRaw) return false;

    const companyData = JSON.parse(companyDataRaw);
    const hasLocalSetup = Boolean(companyData?.localSupabaseURL && companyData?.localSupabaseAnonKey);
    const isVerified = localStorage.getItem("localServerVerified") === "true";

    return hasLocalSetup && !isVerified;
  } catch (err) {
    console.error("[PrivateRoute] Failed to parse local server storage data:", err);
    return false;
  }
};

const PrivateRoute = ({ roles, children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // 1. Wait until AuthContext finishes initialization
  if (loading) {
    return null; // AuthContext already renders AuthLoadingSkeleton
  }

  // 2. Redirect unauthenticated users to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Air-Gapped Network Guard / Server Check
  if (location.pathname !== "/server-check") {
    const requiresServerCheck = checkLocalServerVerificationRequired(currentUser);
    if (requiresServerCheck) {
      console.warn("[PrivateRoute] Local server verification required. Intercepting route.");
      return <Navigate to="/server-check" state={{ from: location }} replace />;
    }
  }

  // 4. Role-based Authorization check
  if (roles && !hasRequiredRole(currentUser.role, roles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // 5. Render children or nested Router Outlet
  return children ? children : <Outlet />;
};

export default PrivateRoute;